import {type IconName} from '@wandb/components';
import * as NetronUtils from '@wandb/weave/common/util/netron';

import {DIFF_FILE_REGEX_WITH_OPTIONAL_SHA} from '../../util/constants';

export type PreviewTypes =
  | 'netron'
  | 'image'
  | 'markdown'
  | 'code'
  | 'notebook'
  | 'artifact'
  | 'patch'
  | 'unknown';

export interface FileInfo {
  type: PreviewTypes;
  language?: string;
  iconName: IconName;
  // some files, like images, can't be copied to clipboard because they don't
  // end up pasting correctly
  copyable: boolean;
}

export function fileInfoFromName(fileName: string): FileInfo {
  if (NetronUtils.isViewable(fileName)) {
    return {type: 'netron', iconName: 'model', copyable: false};
  }
  if (fileName.match(/:v\d+$/)) {
    return {type: 'artifact', iconName: 'artifact-type', copyable: false};
  }
  if (fileName.match(DIFF_FILE_REGEX_WITH_OPTIONAL_SHA)) {
    // Match diff.patch and diff_{sha}.patch
    return {type: 'patch', iconName: 'diff', language: 'diff', copyable: true};
  }

  if (fileName.match(/Dockerfile$/)) {
    return {
      type: 'code',
      language: 'dockerfile',
      iconName: 'document',
      copyable: true,
    };
  }

  const extension = fileName.split('.').pop();
  switch (extension) {
    case 'md':
      return {
        iconName: 'code-html',
        type: 'markdown',
        copyable: true,
      };
    case 'log':
    case 'text':
    case 'txt':
      return {
        iconName: 'document',
        type: 'code',
        copyable: true,
      };
    case 'diff':
    case 'patch':
      return {
        iconName: 'code-json',
        type: 'code',
        language: 'diff',
        copyable: true,
      };
    case 'py':
      return {
        iconName: 'code-json',
        type: 'code',
        language: 'python',
        copyable: true,
      };
    case 'ipynb':
      return {
        iconName: 'code-json',
        type: 'notebook',
        language: 'python',
        copyable: true,
      };
    case 'yml':
    case 'yaml':
      return {
        iconName: 'code-json',
        type: 'code',
        language: 'yaml',
        copyable: true,
      };
    case 'xml':
      return {
        iconName: 'code-html',
        type: 'code',
        language: 'xml',
        copyable: true,
      };
    case 'html':
    case 'htm':
      return {
        iconName: 'code-html',
        type: 'code',
        language: 'html',
        copyable: true,
      };
    case 'sh':
    case 'json':
    case 'css':
    case 'js':
      return {
        iconName: 'code-json',
        type: 'code',
        language: extension,
        copyable: true,
      };
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'tiff':
    case 'tif':
    case 'gif':
      return {type: 'image', iconName: 'photo-picture', copyable: false};
  }
  return {type: 'unknown', iconName: 'document', copyable: true};
}
